import { $isCodeNode } from "@lexical/code-core";
import {
  $isAutoLinkNode,
  AutoLinkNode,
  autoLinkEmailMatcher,
  autoLinkUrlMatcher,
  LinkExtension,
  registerAutoLink,
  TOGGLE_LINK_COMMAND,
  type LinkMatcher,
} from "@lexical/link";
import { mergeRegister, objectKlassEquals } from "@lexical/utils";
import {
  $getSelection,
  $hasUpdateTag,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_NORMAL,
  defineExtension,
  HISTORY_MERGE_TAG,
  PASTE_COMMAND,
  safeCast,
  type LexicalExtension,
} from "lexical";
import { checkUrl, DEFAULT_URL_POLICY, type UrlPolicy } from "../../core/url-policy";

export interface LinkPolicyConfig {
  urlPolicy: UrlPolicy;
  /** Pasting a URL over selected text links the text instead of replacing it. */
  linkPastedUrls: boolean;
  /** URLs and email addresses become links as they are typed or pasted. */
  autoLink: boolean;
}

const EXPLICIT_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Something a person would call a URL: one token with a scheme of its own that the policy lets
 * through. The policy alone is not enough, because it accepts relative targets, and every plain
 * word is a well-formed relative URL.
 */
const isPastedUrl = (text: string, policy: UrlPolicy): boolean => {
  const url = text.trim();
  return !/\s/.test(url) && EXPLICIT_SCHEME.test(url) && checkUrl(url, policy).ok;
};

/**
 * The URL policy for links made in the editor: by command, by pasting a URL over a selection, and
 * by typing one (`AutoLinkNode`, which this also registers; the `autolink` guard in core has always
 * accepted it, but until C1 no preset could load one).
 *
 * `LinkExtension` takes a `validateUrl` for both jobs, and with it registers a paste listener that
 * links the selection to whatever was pasted if `validateUrl` accepts it. Given this package's
 * policy that was any word at all: selecting "world" and pasting "there" left "world" linked to
 * `there` instead of replacing it. So upstream gets no validator, and both jobs are done here.
 */
export const LinkPolicyExtension: LexicalExtension<
  LinkPolicyConfig,
  "z-editor/link-policy",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/link-policy",
  config: safeCast<LinkPolicyConfig>({
    urlPolicy: DEFAULT_URL_POLICY,
    linkPastedUrls: true,
    autoLink: true,
  }),
  nodes: [AutoLinkNode],
  dependencies: [LinkExtension],
  register(editor, config) {
    /**
     * Upstream's matchers, behind the URL policy, and silent while a document is being opened.
     * Lexical 0.51 runs transforms over a freshly parsed state, so without the second condition
     * opening a note with a URL typed out in it would link the URL, which is an edit nobody made.
     * Such a URL is linked the next time its text is touched.
     */
    const allowed =
      (matcher: LinkMatcher): LinkMatcher =>
      (text) => {
        if ($hasUpdateTag(HISTORY_MERGE_TAG)) return null;
        // A match the policy rejects is skipped, not the end of the search.
        for (let from = 0; from < text.length;) {
          const match = matcher(text.slice(from));
          if (match === null) return null;
          if (checkUrl(match.url, config.urlPolicy).ok)
            return { ...match, index: match.index + from };
          from += match.index + Math.max(match.length, 1);
        }
        return null;
      };
    return mergeRegister(
      config.autoLink
        ? registerAutoLink(editor, {
            matchers: [allowed(autoLinkUrlMatcher), allowed(autoLinkEmailMatcher)],
            changeHandlers: [],
            excludeParents: [$isCodeNode],
          })
        : () => {},
      // Inline code is literal too, but it is a format, not a parent. An automatic link whose text
      // is all code is switched off, which is upstream's own state for a link the user declined.
      editor.registerNodeTransform(AutoLinkNode, (node) => {
        if (!$isAutoLinkNode(node) || node.getIsUnlinked()) return;
        const texts = node.getAllTextNodes();
        if (texts.length > 0 && texts.every((text) => text.hasFormat("code"))) {
          node.setIsUnlinked(true);
        }
      }),
      editor.registerCommand(
        TOGGLE_LINK_COMMAND,
        (payload) => {
          const url = typeof payload === "string" ? payload : (payload?.url ?? null);
          // Handled, by doing nothing: a target the policy rejects never reaches upstream.
          return url !== null && !checkUrl(url, config.urlPolicy).ok;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!config.linkPastedUrls || !objectKlassEquals(event, ClipboardEvent)) return false;
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || selection.isCollapsed()) return false;
          const text = event.clipboardData?.getData("text/plain") ?? "";
          if (!isPastedUrl(text, config.urlPolicy)) return false;
          // Only over plain text within a block: not across blocks, code, or tokens.
          const simple = selection
            .getNodes()
            .every((node) => !$isElementNode(node) && $isTextNode(node) && node.isSimpleText());
          if (!simple) return false;
          event.preventDefault();
          return editor.dispatchCommand(TOGGLE_LINK_COMMAND, text.trim());
        },
        COMMAND_PRIORITY_NORMAL,
      ),
    );
  },
});
